import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { X } from 'lucide-react';

interface QRScannerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const QRScanner = ({ open, onOpenChange }: QRScannerProps) => {
  const [isScanning, setIsScanning] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open && !isScanning) {
      startScanner();
    }

    return () => {
      if (scannerRef.current && isScanning) {
        stopScanner();
      }
    };
  }, [open, isScanning]);

  const startScanner = async () => {
    try {
      // Attendre que le DOM soit prêt
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Vérifier que l'élément existe
      const element = document.getElementById('qr-reader');
      if (!element) {
        throw new Error('QR reader element not found');
      }

      const scanner = new Html5Qrcode('qr-reader');
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        (decodedText) => {
          handleScan(decodedText);
        },
        () => {
          // Erreur de scan, on ignore
        }
      );

      setIsScanning(true);
    } catch (error) {
      console.error('Error starting scanner:', error);
      toast({
        title: 'Erreur',
        description: "Impossible d'accéder à la caméra. Vérifiez les permissions.",
        variant: 'destructive',
      });
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current = null;
        setIsScanning(false);
      } catch (error) {
        console.error('Error stopping scanner:', error);
      }
    }
  };

  const handleScan = async (decodedText: string) => {
    let orderId: string;
    
    // Si c'est une URL complète
    if (decodedText.startsWith('http')) {
      try {
        const url = new URL(decodedText);
        orderId = url.pathname.split('/').pop() || '';
      } catch {
        orderId = decodedText.trim();
      }
    } 
    // Si c'est l'ancien format QR-{id}
    else if (decodedText.startsWith('QR-')) {
      orderId = decodedText.replace('QR-', '').trim();
    } 
    // Si c'est juste l'ID
    else {
      orderId = decodedText.trim();
    }

    await stopScanner();
    onOpenChange(false);
    navigate(`/driver/order/${orderId}`);
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" aria-describedby="qr-scanner-description">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-[#37699E] font-['Lexend_Deca']">
            Scanner le QR Code
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div id="qr-scanner-description" className="text-sm text-[#5A6B7A]">
            <p className="mt-1">Scannez le QR code présent sur le bon de commande</p>
          </div>

          <div className="space-y-4">
            <div
              id="qr-reader"
              className="border-2 border-[#FFBF07] rounded-xl overflow-hidden"
              style={{ width: '100%' }}
            />

            <Button
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="w-full"
            >
              <X className="mr-2" size={18} />
              Annuler
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
